import React, { useContext } from 'react'
import styled from 'styled-components'
import moment from 'moment'

import Flex from '@jarvis.oddle/core/components/Flex'
import Div from '@jarvis.oddle/core/components/Div'
import Dropdown from '@jarvis.oddle/core/components/Dropdown'
import IconArrow from '@jarvis.oddle/core/components/Icons/IconArrow'
import IconTriangle from '@jarvis.oddle/core/components/Icons/IconTriangle'
import { H4, P } from '@jarvis.oddle/core/components/Typo'
import { colors } from '@jarvis.oddle/core/theme/default'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'

import { add } from 'src/utils/date'
import useAppSelector from 'src/hooks/useAppSelector'
import { useAppContext } from 'src/containers/AppContext'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { fetchAvailableDateAction, fetchTimeslotAction } from 'src/redux/reservation'

const DropdownContainer = styled(Div)`
  position: absolute;
  top: 100%;
  width: 100%;
  max-height: 60vh;
  overflow: auto;
  min-width: 280px;
  padding: 10px;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  z-index: 10;
  text-align: center;
  ${(props) => (props.show ? 'display: table;' : 'display: none;')}
`

const CaptionContainer = styled.div`
  display: table-caption;
  text-align: center;
  height: 0.5em;
`

const Caption = ({
  date,
  localeUtils,
  locale
}: {
  date: Date
  localeUtils: {
    formatMonthTitle: (date, locale) => string
  }
  locale: string
}): JSX.Element => (
  <CaptionContainer>
    <Flex height='100%' alignItems='center' justifyContent='center'>
      <P bold textAlign='center' normalised color={colors.text}>
        {localeUtils.formatMonthTitle(date, locale)}
      </P>
    </Flex>
  </CaptionContainer>
)

const NakedButton = styled.button.attrs(() => ({
  type: 'button' // To prevent form submit when DatePicker is placed inside a form
}))<{ block?: boolean }>`
  border: none;
  box-shadow: none;
  background: transparent;
  outline: 0;
  cursor: pointer;
  padding: 0;
  ${(props) =>
    props.block
      ? `
    width: 100%;
    display: block;
  `
      : ''}
`

const NavBarContainer = styled(Flex).attrs(() => ({
  justifyContent: 'space-between'
}))`
  position: absolute;
  pointer-events: none;
  width: 100%;
  color: ${colors.text};
  height: 0.5em;
`

const StyledNakedButton = styled(NakedButton)<{ blur: boolean }>`
  pointer-events: all;
  opacity: ${(props) => (props.blur ? 0.2 : 1)};
  cursor: ${(props) => (props.blur ? 'default' : 'pointer')};
`

const NavBar = ({
  fromMonth,
  toMonth,
  previousMonth,
  nextMonth,
  onPreviousClick,
  onNextClick
}: {
  fromMonth: Date
  toMonth: Date
  previousMonth: number
  nextMonth: number
  onPreviousClick: () => void
  onNextClick: () => void
}) => (
  <NavBarContainer>
    <StyledNakedButton
      data-testid='datePicker.prevButton'
      onClick={() => onPreviousClick()}
      blur={previousMonth < fromMonth.setHours(12)}
    >
      <IconArrow.Left fill={colors.text} />
    </StyledNakedButton>
    <StyledNakedButton
      data-testid='datePicker.nextButton'
      onClick={() => onNextClick()}
      blur={toMonth.setHours(12) < nextMonth}
    >
      <IconArrow.Right fill={colors.text} />
    </StyledNakedButton>
  </NavBarContainer>
)

const StyledDropdownLabel = styled(Flex).attrs(() => ({
  role: 'button',
  justifyContent: 'space-between',
  alignItems: 'center'
}))`
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #d6d6d6;
  box-sizing: border-box;
  border-radius: 2px;
  padding: 0 10px;
`

const StyledIcon = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  alignItems: 'end'
}))`
  margin-right: 10px;
`

const StyledTextContainer = styled(Flex)`
  align-items: start;
  display: contents;
`

const StyledDatetimeSelected = styled(H4)`
  margin: 21px 11px 8px;
  padding-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #000;
`

const StyledLabel = styled(P)`
  position: absolute;
  top: 0;
  font-weight: 400;
  font-size: 11px !important;
  line-height: 12px !important;
  color: #bbbbbb;
  padding-left: 10px;
`

const DatetimeDropdown = (): JSX.Element => {
  const { formatFullDate } = useAppContext()
  const { t } = useContext(TranslationContext)
  const dispatch = useAppDispatch()
  const maxDaysPickerConfig = useAppSelector((state) => state['@meta'].detailMenuSite.data.maxDaysPickerConfig)
  const blockedDate = useAppSelector((state) => state['@reservation'].blockedDate.data) || []
  const { values } = useFormContext()

  const disabledDays = [
    ...blockedDate.map(blockedData => new Date(blockedData.date)),
    {
      before: new Date(),
      after: add(new Date(), maxDaysPickerConfig, 'day').toDate()
    }
  ]

  const onSelectedDateChange = async ({
    value: selectedDate,
    setFieldValue,
    closeDropdown
  }) => {
    setFieldValue('date', selectedDate, false)
    closeDropdown()
    if (values?.outlet?.id) {
      const newTimeslot = await dispatch(fetchTimeslotAction({
        outletId: values.outlet.id,
        pax: values.numberOfChildren + values.numberOfAdult,
        date: moment(new Date(selectedDate)).format('YYYY-MM-DD')
      }))
      if (newTimeslot?.payload?.[0]) {
        setFieldValue('slot', newTimeslot.payload[0], false)
      }
    }
  }

  const handleChangeMonth = async (month) => {
    await dispatch(fetchAvailableDateAction({
      outletId: values.outlet.id,
      pax: values.numberOfChildren + values.numberOfAdult,
      month: moment(new Date(month)).format('YYYY-MM')
    }))
  }

  return (
    <Dropdown
      renderLabel={({ toggleDropdown }) => (
        <StyledDropdownLabel
          data-testid='datetimeReserveDropdown.label'
          onClick={toggleDropdown}
        >
          <StyledTextContainer>
            <StyledLabel>{t('Select Month')}</StyledLabel>
            <StyledDatetimeSelected>{formatFullDate(values.date)}</StyledDatetimeSelected>
          </StyledTextContainer>
          <StyledIcon>
            <IconTriangle iconSize='medium' fill='#000' />
          </StyledIcon>
        </StyledDropdownLabel>
      )}
      renderDropdown={({ isDropdownOpening, closeDropdown }) =>
        isDropdownOpening ? (
          <>
            <Form.DatePicker
              name='date'
              resetOnUnmount={false}
              fieldValidateOnChange={false}
              disabledDays={disabledDays}
              onChange={(params) =>
                onSelectedDateChange({ ...params, closeDropdown })
              }
              customCaption={Caption}
              CustomNavBar={NavBar}
              onMonthChange={handleChangeMonth}
            />
          </>
        ) : null
      }
      DropdownContainer={DropdownContainer}
    />
  )
}

export default DatetimeDropdown
